import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import { DropdownItem } from 'reactstrap';
import Isvg from 'react-inlinesvg';
import classnames from 'classnames';
import history from 'app/routes/history';
import IconModal from 'app/components/general/IconModal';
import Table from 'app/components/general/TableNew';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import DeleteIcon from 'app/images/icons/delete.svg';
import EditIcon from 'app/images/icons/edit.svg';
import StatusBadge from 'app/components/general/StatusBadge';
import PaymentBadge from 'app/components/general/PaymentBadge';
import moment from 'moment';

import { getGroupSummary } from 'app/ducks/groups/selectors';
import { IGroup } from 'app/ducks/groups/types';
import CreateTeam from 'app/components/teams/CreateTeam';

import ImportIcon from 'app/images/icons/upload.svg';

interface IProps {
  actions: any;
  groupsById: IGroup[];
  groupIds: string[];
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectTeam: (value) => void;
}

const TeamsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  groupsById: undefined,
  groupIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  selectTeam: undefined,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teamId, setTeamId] = useState(undefined);

  const filterOptions = [
    { label: 'Team', accessor: 'name', type: 'text' },
    { label: 'Competition/s', accessor: 'competitionName', type: 'text' },
    { label: 'Season/s', accessor: 'seasonName', type: 'text' },
    { label: 'Section/s', accessor: 'sectionName', type: 'text' },
    {
      label: 'Owing',
      accessor: 'financialStatus',
      type: 'select',
      options: [
        { id: 'paid', name: 'Up-to-date' },
        { id: 'owing', name: 'Owing' },
        { id: 'overdue', name: 'Overdue' },
      ],
    },
    { label: 'Created', accessor: 'createdAt', type: 'datetime' },
    { label: 'Updated', accessor: 'updatedAt', type: 'datetime' },
  ];
  const quickFilterOptions = [
    { label: 'Team', accessor: 'name', type: 'text' },
    {
      label: 'Owing',
      accessor: 'financialStatus',
      type: 'select',
      options: [
        { id: 'paid', name: 'Up-to-date' },
        { id: 'owing', name: 'Owing' },
        { id: 'overdue', name: 'Overdue' },
      ],
    },
  ];
  // { label: '# of team contacts', accessor: 'numberOfPeople', type: 'number' },

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllGroups(payload);
  };

  const handleOpenModal = (e, selectedTeamId): void => {
    e.preventDefault();
    setDeleteModalOpen(true);
    setTeamId(selectedTeamId);
  };

  const handeCloseModal = (e): void => {
    e.preventDefault();
    setDeleteModalOpen(false);
    setTeamId(undefined);
  };

  const handleOpenEditModal = (e, selectedTeamId): void => {
    e.preventDefault();
    props.actions.selectTeam(selectedTeamId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = (e): void => {
    e.preventDefault();
    setEditModalOpen(false);
  };

  const handleOpenCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(true);
  };

  const handeCloseCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(false);
  };

  const handleDelete = (e): void => {
    e.preventDefault();
    props.actions.deleteTeam({ id: teamId });
  };

  const renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title="Delete Team Member"
      icon={DeleteIcon}
      description="Are you sure you want to delete this team member?"
      buttonText="Delete"
      isOpen={deleteModalOpen}
      isLoading={props.isLoading}
      handleSubmit={(e) => handleDelete(e)}
      handleCancel={handeCloseModal}
    />
  );

  const handleClick = (id: string): void => {
    // props.actions.selectTeam(id);
    history.push(`/teams/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.groupsById) || props.groupIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.groupIds,
      (obj, id) => {
        if (!_.isEmpty(props.groupsById[id])) {
          obj.push(getGroupSummary(props.groupsById[id]));
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.groupsById, props.groupIds]);

  const handleImportTeams = (): void => {
    history.push(
      {
        pathname: '/import/teams',
        state: { modal: true, previousPath: props.match.path },
      },
    );
  };

  const smallColumn = 'col col-2 col-lg-2';
  const flexColumn = 'col col-2 d-none d-md-flex col-xl-1';
  const mediumColumn = 'col col-6 col-md-3 col-lg-3 col-xl-2';

  const columns = useMemo(() => [
    {
      Header: 'Team',
      accessor: 'groupName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
    {
      Header: 'Owing',
      className: smallColumn,
      headerClassName: smallColumn,
      disableSortBy: true,
      id: 'financialStatus',
      accessor: (row) => ({
        financialStatusHumanised: row.financialStatusHumanised,
        financialStatusColour: row.financialStatusColour,
      }),
      Cell: (cellProps) => (
        <PaymentBadge
          className="event-card-badge"
          paymentText={cellProps.value.financialStatusHumanised}
        />
      ),
    },
    {
      Header: 'Competition/s',
      accessor: 'competitionName',
      className: smallColumn,
      headerClassName: smallColumn,
    },
    {
      Header: 'Season/s',
      accessor: 'seasonName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
    {
      Header: 'Section/s',
      accessor: 'sectionName',
      className: mediumColumn,
      headerClassName: mediumColumn,
    },
    {
      Header: 'Updated',
      accessor: 'updatedAt',
      className: mediumColumn,
      headerClassName: mediumColumn,
      Cell: (cellProps) => moment(cellProps.value).format('D MMM YYYY'),
    },
  ], []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 team' : `${props.pagination.meta.totalCount || 0} teams`;

  return (
    <div>
      {renderDeleteModal()}
      <IconModal
        title="Create Team"
        icon={PlusIcon}
        handleCancel={handeCloseCreateModal}
        isOpen={createModalOpen}
      >
        <CreateTeam {...props} teamId={props.teamId} afterSubmit={handeCloseCreateModal} />
      </IconModal>
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        // aggressiveLoader
        headerTitle={props.headerTitle}
        objectTitle="teams"
        filterTitle="Teams Data"
        counterText={counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        quickFilterOptions={quickFilterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      >
        <Button onClick={handleImportTeams} noLoading className="btn-thinner btn-2">
          <Isvg src={ImportIcon} className="btn-icon" />
          Import
        </Button>
        {/* <Button onClick={handleOpenCreateModal} noLoading className="btn-thinner btn-2">
          <Isvg src={PlusIcon} className="btn-icon" />
          New Team
        </Button> */}
      </Table>
    </div>
  );
};

export default TeamsTable;
